import type { ExperienceDto, MeDto, SpaceDto } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';

// Onboarding asks one light question; everything else can be changed later.
const FEATURED = ['general', 'student', 'teacher', 'parent', 'ceo', 'specialist'];

export function OnboardingPage() {
  const { i18n, setMe } = useApp();
  const { t, locale } = i18n;
  const navigate = useNavigate();
  const [personal, setPersonal] = useState<SpaceDto | null>(null);
  const [options, setOptions] = useState<ExperienceDto['availablePersonas']>([]);
  const [selected, setSelected] = useState('general');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { spaces } = await api<{ spaces: SpaceDto[] }>('GET', '/spaces');
        const p = spaces.find((s) => s.type === 'personal') ?? null;
        setPersonal(p);
        if (p) {
          const exp = await api<ExperienceDto>('GET', `/spaces/${p.id}/experience`);
          setOptions(exp.availablePersonas.filter((o) => FEATURED.includes(o.key)));
          setSelected(p.activePersonaKey);
        }
      } catch (err) {
        setError(t(errorMessageKey(err)));
      }
    })();
  }, [t]);

  const finish = async (skip: boolean) => {
    try {
      if (!skip && personal) await api('PUT', `/spaces/${personal.id}/persona`, { personaKey: selected });
      setMe(await api<MeDto>('PATCH', '/me', { onboardingCompleted: true }));
      navigate('/', { replace: true });
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  return (
    <main className="auth-page" id="main">
      <div className="card stack" style={{ width: 'min(720px, 100%)' }}>
        <h1>{t('onboarding.title')}</h1>
        <p className="muted">{t('onboarding.subtitle')}</p>
        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}
        <div className="persona-grid" role="group" aria-label={t('onboarding.title')}>
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              className="persona-option"
              aria-pressed={selected === o.key}
              onClick={() => setSelected(o.key)}
            >
              <strong>{o.label[locale] ?? o.label.tr}</strong>
              <span className="muted">{o.description[locale] ?? o.description.tr}</span>
            </button>
          ))}
        </div>
        <p className="hint">{t('onboarding.personaNote')}</p>
        <div className="row">
          <button className="btn" type="button" onClick={() => void finish(true)}>
            {t('onboarding.skip')}
          </button>
          <span className="spacer" />
          <button className="btn btn-primary" type="button" onClick={() => void finish(false)} disabled={!personal}>
            {t('onboarding.continue')}
          </button>
        </div>
      </div>
    </main>
  );
}
